import { MatchResponseDto } from "@/lib/api/interfaces/matches.interface";
import { useMatchesDelete, useMatchesFindQuery as useMatchesFind, UseMatchesFindParams, useMatchesBulkDelete } from "@/lib/api/requests/matches.requests";
import { formatDate } from "@/lib/utils/date-formatter";
import {
  DataView,
  DataViewContent,
  DataViewEmptyState,
  DataViewGridCard,
  DataViewLayout,
  DataViewListCard,
  DataViewLoadingState,
  DataViewLoadMore,
  DataViewToolbar,
  GenericItem,
  GenericItemActions
} from "@/util-components/data-view";
import { AdvancedFilterDef, AdvancedSortDef, AdvancedState, SortDirection } from "@/util-components/data-view/components/advanced-filter-types";
import { PageHeader, PageLayout, PageTitle, PrimaryActionButton } from "@/util-components/page-layout";
import { StandardDeleteDialog } from "@/util-components/dialog/StandardDeleteDialog";
import { Calendar, MapPin, Plus, Shield, Swords, Trophy, Users } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "@/lib/toast";
import { usePageState } from "@/lib/hooks/use-page-state";
import { cn } from "@/lib/utils/cn";
import CreateMatchDialog from "./create-dialog";
import MatchDetailsDialog from "./details-dialog";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";

const DEFAULT_PAGE_SIZE = 20;

export const getMatchResult = (match: MatchResponseDto) => {
  if (
    match.ourScore !== undefined &&
    match.opponentScore !== undefined &&
    match.ourScore !== null &&
    match.opponentScore !== null
  ) {
    if (match.homeOrAway === 'AWAY') {
      return `${match.opponentScore} : ${match.ourScore}`;
    }
    return `${match.ourScore} : ${match.opponentScore}`;
  }
  return "-";
};

export const getMatchStatus = (match: MatchResponseDto) => {
  if (
    match.ourScore !== undefined &&
    match.opponentScore !== undefined &&
    match.ourScore !== null &&
    match.opponentScore !== null
  ) {
    if (match.ourScore > match.opponentScore) {
      return { 
        text: "Gewonnen", 
        color: "text-emerald-700 bg-emerald-50/80 border-emerald-100", 
        badgeVariant: "default" as const,
        scoreColor: "text-emerald-700",
        bgColor: "bg-emerald-50",
        solidBg: "bg-emerald-50"
      };
    } else if (match.ourScore < match.opponentScore) {
      return { 
        text: "Verloren", 
        color: "text-rose-700 bg-rose-50/80 border-rose-100", 
        badgeVariant: "destructive" as const,
        scoreColor: "text-rose-700",
        bgColor: "bg-rose-50",
        solidBg: "bg-rose-50"
      };
    } else {
      return { 
        text: "Unentschieden", 
        color: "text-amber-700 bg-amber-50/80 border-amber-100", 
        badgeVariant: "secondary" as const,
        scoreColor: "text-amber-700",
        bgColor: "bg-amber-50",
        solidBg: "bg-amber-50"
      };
    }
  }
  return { 
    text: "Ausstehend", 
    color: "text-slate-600 bg-slate-50/80 border-slate-100", 
    badgeVariant: "outline" as const,
    scoreColor: "text-slate-600",
    bgColor: "bg-slate-50",
    solidBg: "bg-slate-50"
  };
};

const MatchesWrapper = () => {
    return (
        <PageLayout
            basePath="/spiele"
            suspenseType="blue"
        >
            <PageHeader>
                <PageTitle
                    title="Spiele"
                    icon={Swords}
                    iconColor="text-blue-600"
                    iconBg="bg-blue-50"
                />

            </PageHeader>
        </PageLayout>
    );
};

export const MatchesList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state, setSettings, setViewMode } = usePageState('matches-page', { 
    viewMode: 'grid', 
    settings: { 
      advancedState: { 
        filters: [], 
        sorts: [{ key: 'date', direction: 'asc' as SortDirection }] 
      } as AdvancedState 
    } 
  });
  const viewMode = state.viewMode || 'grid';
  // Advanced state integration - Always used
  const advancedState = useMemo<AdvancedState>(() => {
    const s = (state.settings?.advancedState as AdvancedState);
    if (!s) return { filters: [], sorts: [{ key: 'date', direction: 'asc' as SortDirection }] };
    // Enforce default sort if empty (prevent "stuck" state with disableMultiSort)
    if (!s.sorts || s.sorts.length === 0) {
       return { ...s, sorts: [{ key: 'date', direction: 'asc' as SortDirection }] };
    }
    return s;
  }, [state.settings?.advancedState]);

  const [searchTerm, setSearchTerm] = useState(searchParams.get('textSearch') || "");
  const [currentPage, setCurrentPage] = useState(1);
  const [allMatches, setAllMatches] = useState<MatchResponseDto[]>([]);
  const [lastFilterKey, setLastFilterKey] = useState<string>('');
  const [teamOptions, setTeamOptions] = useState<{ label: string; value: string }[]>([]);
  const [leagueOptions, setLeagueOptions] = useState<{ label: string; value: string }[]>([]);

  // Dialog states
  const [detailsPopupOpen, setDetailsPopupOpen] = useState<{
    open: boolean;
    match: MatchResponseDto | null;
    mode: "details" | "edit";
  }>({
    open: false,
    match: null,
    mode: "details",
  });

  const [createMatchDialogOpen, setMatchDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; match: MatchResponseDto | null; isBulk?: boolean }>({
    open: false,
    match: null,
    isBulk: false
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { mutate: deleteMatch, isPending: isDeleting } = useMatchesDelete();
  const { mutate: bulkDeleteMatches, isPending: isBulkDeleting } = useMatchesBulkDelete();

  // Filter params
  const currentFilterKey = useMemo(() => {
    // Only include filters that actually have a value
    const activeFilters = advancedState.filters.filter(f => f.value && (Array.isArray(f.value) ? f.value.length > 0 : true));
    return `${searchTerm.trim()}-${JSON.stringify(activeFilters)}-${JSON.stringify(advancedState.sorts)}`;
  }, [searchTerm, advancedState]);

  const params: UseMatchesFindParams = useMemo(() => {
    // Base params
    const baseParams: UseMatchesFindParams = {
        textSearch: searchTerm.trim() || undefined,
        page: currentPage,
        limit: DEFAULT_PAGE_SIZE,
    };

    // Advanced overrides
    const teamFilter = advancedState.filters.find(f => f.key === 'teamId');
    if (teamFilter && teamFilter.value) {
        baseParams.teamId = teamFilter.value;
    }

    const homeAwayFilter = advancedState.filters.find(f => f.key === 'homeOrAway');
    if (homeAwayFilter && homeAwayFilter.value) {
        baseParams.homeOrAway = homeAwayFilter.value as "HOME" | "AWAY";
    }

    const leagueFilter = advancedState.filters.find(f => f.key === 'leagueName');
    if (leagueFilter && leagueFilter.value) {
        baseParams.leagueName = leagueFilter.value as string;
    }

    const statusFilter = advancedState.filters.find(f => f.key === 'status');
    if (statusFilter && statusFilter.value && Array.isArray(statusFilter.value)) {
        const values = statusFilter.value as string[];
        if (values.length === 1) {
            if (values.includes('past')) baseParams.filterType = 'past';
            if (values.includes('upcoming')) baseParams.filterType = 'upcoming';
        } else {
            baseParams.filterType = 'all';
        }
    } else {
        baseParams.filterType = 'all';
    }

    // Map advanced sorts
    if (advancedState.sorts.length > 0) {
        const primarySort = advancedState.sorts[0];
        if (primarySort) {
            baseParams.sort = primarySort.key;
            baseParams.order = primarySort.direction;
        }
    }

    return baseParams;
  }, [searchTerm, currentPage, advancedState]);

  const { data, isLoading, refetch, isError } = useMatchesFind(params, {
    skipGlobalErrorHandler: true
  });

  // Show toast on error
  useEffect(() => {
    if (isError) {
      toast.error("Fehler beim Laden der Spiele");
    }
  }, [isError]);

  // Reset on filter change
  useEffect(() => {
    if (currentFilterKey !== lastFilterKey) {
      setCurrentPage(1);
      setAllMatches([]);
      setLastFilterKey(currentFilterKey);
    }
  }, [currentFilterKey, lastFilterKey]);

  // Accumulate data
  useEffect(() => {
    if (data?.matches?.docs) {
      if (currentPage === 1) {
        setAllMatches(data.matches.docs);
      } else {
        setAllMatches(prev => {
          const newIds = new Set(data.matches.docs.map(d => d._id));
          return [...prev.filter(p => !newIds.has(p._id)), ...data.matches.docs];
        });
      }
    }
    
    if (data?.listEntities?.teams) {
      setTeamOptions(data.listEntities.teams.map(t => ({
        label: t.value,
        value: t._id
      })));
    }

    if (data?.listEntities?.leagueRecommendations) {
      const options = data.listEntities.leagueRecommendations.map(l => ({
        label: l,
        value: l
      }));
      // Add "No Competition" option
      options.unshift({ label: 'Kein Wettbewerb', value: 'NO_LEAGUE' });
      setLeagueOptions(options);
    }
  }, [data, currentPage]);

  // Sync URL params
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (searchTerm) {
      newParams.set('textSearch', searchTerm);
    } else {
      newParams.delete('textSearch');
    }
    
    // Note: Advanced state is persisted in localStorage via usePageState, not URL
    
    setSearchParams(newParams, { replace: true });
  }, [searchTerm, setSearchParams, searchParams]);

  const hasMore = data?.matches?.hasNextPage || false;
  const isInitialLoading = (isLoading || (!!data?.matches?.docs?.length && allMatches.length === 0)) && currentPage === 1;

  const isLoadingMore = isLoading && currentPage > 1;

  const handleLoadMore = () => {
    if (hasMore && !isLoadingMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleSearchChange = (text: string) => {
    setSearchTerm(text);
  };

  const handleDelete = async () => {
    if (deleteConfirm.isBulk && selectedIds.length > 0) {
      await bulkDeleteMatches({ ids: selectedIds }, {
        onSuccess: async () => {
            toast.success(`${selectedIds.length} Spiele erfolgreich gelöscht`);
            await refetch();
            setCurrentPage(1); 
            setAllMatches(prev => prev.filter(m => !selectedIds.includes(m._id)));
            setSelectedIds([]);
            setDeleteConfirm({ open: false, match: null, isBulk: false });
        },
        onError: (error) => {
            toast.error("Spiele konnten nicht gelöscht werden", { error });
        }
      });
      return;
    }

    if (!deleteConfirm.match) return;

    deleteMatch({ id: deleteConfirm.match._id }, {
      onSuccess: async () => {
          toast.success("Spiel erfolgreich gelöscht");
          await refetch();
          setCurrentPage(1); 
          setAllMatches(prev => prev.filter(m => m._id !== deleteConfirm.match!._id));
          setDeleteConfirm({ open: false, match: null });
      },
      onError: (e) => {
          console.log(e);
          toast.error("Spiel konnte nicht gelöscht werden", { error: e });
      }
    });
  };

  const handleSelectionDelete = () => {
    if (selectedIds.length > 0) {
      setDeleteConfirm({ open: true, match: null, isBulk: true });
    }
  };

  const handleCreateSuccess = async () => {
    await refetch();
    setCurrentPage(1);
  };

  const handleDetailsClose = async (shouldRefetch?: boolean) => {
    setDetailsPopupOpen({ open: false, match: null, mode: "details" });
    if (shouldRefetch) {
      await refetch();
      setCurrentPage(1);
    }
  };

  const convertToGenericItem = useCallback((match: MatchResponseDto): GenericItem => {
    const teamName = match.team?.name || "Kein Team";
    const teamLogoUrl = match.team?.thumbnailUrl || match.team?.logoUrl;
    
    const opponentName = match.opponent?.name || "Unbekannter Gegner";
    const opponentLogoUrl = match.opponent?.thumbnailUrl || match.opponent?.logoUrl;
    
    const status = getMatchStatus(match);
    const result = getMatchResult(match);

    const isAway = match.homeOrAway === 'AWAY';
    
    // Logos based on Home/Away
    const leftLogoUrl = isAway ? opponentLogoUrl : teamLogoUrl;
    const rightLogoUrl = isAway ? teamLogoUrl : opponentLogoUrl;

    const title = isAway 
      ? `${opponentName} vs. ${teamName}` 
      : `${teamName} vs. ${opponentName}`;
    
    const typeLabel = isAway ? "Auswärtsspiel" : "Heimspiel";

    return {
      id: match._id,
      title,
      description: `${formatDate(match.date)} • ${typeLabel} • ${match.location}`,
      previewContent: (
        <div className={cn(
          "group relative w-full h-full overflow-hidden transition-all duration-500",
          status.solidBg
        )}>
            {/* Background Logos - Monochrome & Multiplied */}
            <div className="absolute inset-0 pointer-events-none mix-blend-multiply grayscale opacity-[0] blur-[2px] transition-all duration-500 group-hover:opacity-[0.15] group-hover:blur-[1px]">
                {/* Left Logo (Heim) */}
                <div className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 h-[80%] aspect-square transition-all duration-700 ease-in-out group-hover:-translate-x-[40%] group-hover:rotate-[-4deg]">
                    {leftLogoUrl ? (
                        <img 
                            src={leftLogoUrl} 
                            alt=""
                            className="w-full h-full object-contain" 
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <div className="w-full h-full rounded-full border-2 border-black/20 flex items-center justify-center">
                                <Shield className="w-1/2 h-1/2 opacity-20" />
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Logo (Gast) */}
                <div className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 h-[75%] aspect-square transition-all duration-700 ease-in-out group-hover:translate-x-[40%] group-hover:rotate-[4deg]">
                    {rightLogoUrl ? (
                        <img 
                            src={rightLogoUrl} 
                            alt=""
                            className="w-full h-full object-contain" 
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <div className="w-full h-full rounded-full border-2 border-black/20 flex items-center justify-center">
                                <Shield className="w-1/2 h-1/2 opacity-20" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Content Overlay */}
            <div className="relative z-10 w-full h-full flex flex-col items-center justify-center gap-2 p-4">
                
                {/* Score Display */}
                <div className="flex items-center justify-center">
                    <span className={cn(
                      "text-4xl font-bold tracking-tight",
                      status.scoreColor
                    )}>
                        {result !== "-" ? result : "-:-"}
                    </span>
                </div>

                {/* Status Badge */}
                <div className={cn(
                    "px-3 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-widest border shadow-sm backdrop-blur-sm",
                    status.color
                )}>
                    {status.text}
                </div>
            </div>
        </div>
      ),
      previewContentList: (
        <div className={`w-full h-full flex items-center justify-center transition-colors ${status.bgColor}`}>
            <span className={`font-bold tracking-tight ${status.scoreColor}`}>
                {result !== "-" ? result : "-:-"}
            </span>
        </div>
      ),
      metadata: {
        badgeText: match.leagueName
      }
    };
  }, []);

  const genericItems = useMemo(() => {
    return allMatches.map(convertToGenericItem);
  }, [allMatches, convertToGenericItem]);

  const genericActions: GenericItemActions = {
    onItemClick: (id) => {
      const match = allMatches.find(m => m._id === id);
      if (match) setDetailsPopupOpen({ open: true, match, mode: "details" });
    },
    onEdit: (id) => {
      const match = allMatches.find(m => m._id === id);
      if (match) setDetailsPopupOpen({ open: true, match, mode: "edit" });
    },
    onDelete: (id) => {
      const match = allMatches.find(m => m._id === id);
      if (match) setDeleteConfirm({ open: true, match });
    }
  };

  // --- Advanced Defs ---
  const advancedFilterDefs = useMemo<AdvancedFilterDef[]>(() => {
    return [
       {
         key: 'teamId',
         label: 'Mannschaft',
         type: 'select',
         icon: Users,
         options: teamOptions
       },
       {
         key: 'leagueName',
         label: 'Wettbewerb',
         type: 'select',
         icon: Trophy,
         options: leagueOptions
       },
       {
         key: 'homeOrAway',
         label: 'Spielort',
         type: 'select',
         icon: MapPin,
         options: [
            { label: 'Heimspiel', value: 'HOME' },
            { label: 'Auswärtsspiel', value: 'AWAY' }
         ]
       },
       {
         key: 'status',
         label: 'Zeitraum',
         type: 'multi-select',
         icon: Calendar,
         options: [
            { label: 'Vergangene Spiele', value: 'past' },
            { label: 'Zukünftige Spiele', value: 'upcoming' }
         ]
       }
    ];
  }, [teamOptions, leagueOptions]);

  const advancedSortDefs = useMemo<AdvancedSortDef[]>(() => [
     { key: 'date', label: 'Spiel-Datum' },
     { key: 'createdAt', label: 'Hinzugefügt' },
     { key: 'updatedAt', label: 'Zuletzt bearbeitet' }
  ], []);

  const handleAdvancedStateChange = (newState: AdvancedState) => {
    setSettings(prev => ({ ...prev, advancedState: newState }));
  };

  const handleAdvancedReset = () => {
      setSettings(prev => ({ ...prev, advancedState: { filters: [], sorts: [{ key: 'date', direction: 'asc' as SortDirection }] } }));
      setSearchTerm("");
  };

  return (
    <div className="h-full flex flex-col">
      <DataView 
        viewMode={viewMode} 
        onViewModeChange={setViewMode}
        className="flex-1 bg-transparent"
        isLoading={isInitialLoading}
        isEmpty={allMatches.length === 0}
        selectedIds={selectedIds}
        onMultiSelect={setSelectedIds}
        onSelectionDelete={handleSelectionDelete}
      >
        <div className="flex flex-col h-full">
          <DataViewToolbar
            searchQuery={searchTerm}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Spiele suchen..."
            infoText={
              `${data?.matches?.count || 0} Spiele`
            }
            
            // Advanced Props (Always Advanced)
            mode="advanced"
            advancedFilterDefs={advancedFilterDefs}
            advancedSortDefs={advancedSortDefs}
            advancedState={advancedState}
            onAdvancedStateChange={handleAdvancedStateChange}
            onAdvancedReset={handleAdvancedReset}
            defaultSort={{ key: 'date', direction: 'asc' as SortDirection }}

            leading={
              <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                <PrimaryActionButton 
                  onClick={() => setMatchDialogOpen(true)}
                  label="Spiel hinzufügen"
                  icon={Plus}
                />
              </PermissionFeatureWrapper>
            }
          />

          <DataViewLayout>
            <DataViewContent
              items={genericItems}
              renderGridItem={(item) => (
                <DataViewGridCard
                  item={item}
                  actions={genericActions}
                />
              )}
              renderListItem={(item) => (
                <DataViewListCard
                  item={item}
                  actions={genericActions}
                />
              )}
              loadingIndicator={<DataViewLoadingState />}
              emptyState={
                <DataViewEmptyState
                  icon={<Trophy className="h-8 w-8 text-gray-400" />}
                  title={searchTerm ? 'Keine Spiele gefunden' : 'Keine Spiele'}
                  description={searchTerm 
                    ? 'Versuchen Sie andere Suchbegriffe.'
                    : 'Erstellen Sie Ihr erstes Spiel.'
                  }
                  action={
                    !searchTerm ? (
                      <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                        <PrimaryActionButton 
                          onClick={() => setMatchDialogOpen(true)}
                          label="Spiel erstellen"
                          icon={Plus}
                        />
                      </PermissionFeatureWrapper>
                    ) : undefined
                  }
                />
              }
            />
            
            <DataViewLoadMore
               onLoadMore={handleLoadMore}
               isLoadingMore={isLoadingMore}
               hasMore={hasMore}
            />
          </DataViewLayout>
        </div>

        {/* Dialogs */}
        {detailsPopupOpen.open && detailsPopupOpen.match && (
          <MatchDetailsDialog
            open={detailsPopupOpen.open}
            matchId={detailsPopupOpen.match._id}
            mode={detailsPopupOpen.mode}
            onClose={handleDetailsClose}
            listEntities={data?.listEntities}
          />
        )}

        {createMatchDialogOpen && (
          <CreateMatchDialog
            open={createMatchDialogOpen}
            onClose={(refetchData) => {
              setMatchDialogOpen(false);
              if (refetchData) {
                handleCreateSuccess();
              }
            }}
          />
        )}
        
        <StandardDeleteDialog 
          open={deleteConfirm.open} 
          onOpenChange={(val) => setDeleteConfirm(prev => ({ ...prev, open: val }))}
          onDelete={handleDelete}
          title={deleteConfirm.isBulk ? `${selectedIds.length} Spiele löschen` : "Spiel löschen"}
          description={deleteConfirm.isBulk ? "Möchten Sie diese Spiele wirklich unwiderruflich löschen?" : "Diese Aktion kann nicht rückgängig gemacht werden"}
          itemTitle={deleteConfirm.match ? data?.listEntities?.opponents.find(o => o._id === deleteConfirm.match?.belongsToOpponentId)?.value : undefined}
          itemTypeLabel={deleteConfirm.match ? "ID" : undefined}
          itemsList={deleteConfirm.isBulk ? selectedIds.map(id => {
            const m = allMatches.find(m => m._id === id);
            const oppName = m?.opponent?.name;
            return {
              title: m ? `${m.homeOrAway === 'AWAY' ? 'vs ' : ''}${oppName || 'Gegner'}` : id,
              subtitle: m ? formatDate(m.date) : undefined
            };
          }) : undefined}
          isLoading={isDeleting || isBulkDeleting}
        />
      </DataView>
    </div>
  );
};

export default MatchesWrapper;
